import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getApiBaseUrl } from "../config/api";
import { resolveAvatarUrl, handleAvatarError } from "../utils/profilePicUrl";
import { formatAuthorMetaLine } from "../utils/displayName";
import AuthorNameLink from "./shared/AuthorNameLink";
import ArticleCard, { BLOG_CARD_PROPS } from "./shared/ArticleCard";
import BlogForm from "./BlogForm";

const API_URL = getApiBaseUrl();

/**
 * Blogs on the COOL page. Any signed-in account type can publish.
 * Articles stay on their own composer and tab.
 */
export default function BlogBoard({ user, onRefresh }) {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const loadBlogs = useCallback(async () => {
    if (!API_URL) {
      setBlogs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        post_type: "blog",
        limit: "30",
        offset: "0",
      });
      if (user?.id) params.set("viewer_id", String(user.id));
      const res = await fetch(`${API_URL}/get_posts.php?${params.toString()}`);
      const data = await res.json();
      setBlogs(data.success && Array.isArray(data.posts) ? data.posts : []);
    } catch (err) {
      console.error(err);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadBlogs();
  }, [loadBlogs]);

  const linkPrefix = user ? "/home/blog" : "/home/public";

  return (
    <div
      style={{
        maxWidth: 760,
        margin: "0 auto",
        padding: "28px 16px 64px",
      }}
    >
      <div
        style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 16,
          padding: "20px 20px 16px",
          marginBottom: 16,
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
        }}
      >
        <div
          style={{
            display: "inline-block",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            color: "#0f766e",
            background: "#ccfbf1",
            padding: "3px 8px",
            borderRadius: 4,
            marginBottom: 8,
          }}
        >
          Blogs
        </div>
        <h1 style={{ margin: "0 0 8px", fontSize: 28, color: "#0f172a" }}>
          COOL blogs
        </h1>
        <p style={{ margin: "0 0 16px", color: "#475569", lineHeight: 1.55, fontSize: 15 }}>
          Blogs are personal writing on the COOL page. They are separate from articles.
          User, Intern, Employer, and Admin accounts can all publish a blog.
        </p>
        {user ? (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            style={{
              background: "linear-gradient(135deg, #14b8a6, #0f766e)",
              color: "#fff",
              border: "none",
              borderRadius: 999,
              padding: "10px 18px",
              fontWeight: 700,
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            Write a blog
          </button>
        ) : (
          <Link
            to="/login"
            style={{
              display: "inline-block",
              background: "linear-gradient(135deg, #14b8a6, #0f766e)",
              color: "#fff",
              textDecoration: "none",
              borderRadius: 999,
              padding: "10px 18px",
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            Log in to write a blog
          </Link>
        )}
      </div>

      {loading && <p style={{ color: "#64748b" }}>Loading blogs…</p>}

      {!loading && blogs.length === 0 && (
        <p
          style={{
            background: "#fff",
            border: "1px dashed #99f6e4",
            borderRadius: 16,
            padding: 28,
            textAlign: "center",
            color: "#475569",
          }}
        >
          No blogs yet. Be the first to write one.
        </p>
      )}

      {!loading &&
        blogs.map((post) => (
          <div
            key={post.id}
            style={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 16,
              padding: 16,
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <img
                src={resolveAvatarUrl(post, API_URL)}
                alt=""
                onError={handleAvatarError}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
              <div>
                <AuthorNameLink user={post} style={{ fontWeight: 700, color: "#0f172a" }} />
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {formatAuthorMetaLine(post, (value) =>
                    value ? new Date(value).toLocaleString() : ""
                  )}
                </div>
              </div>
            </div>
            <ArticleCard post={post} linkPrefix={linkPrefix} {...BLOG_CARD_PROPS} />
          </div>
        ))}

      {showForm && (
        <BlogForm
          user={user}
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            loadBlogs();
            onRefresh?.();
          }}
          onRefresh={() => {
            loadBlogs();
            onRefresh?.();
          }}
        />
      )}
    </div>
  );
}
